import React, { useEffect, useState, useRef } from "react";
import { Navigate } from "react-router-dom";
import { api, fmtNum, fmtDate, errMsg } from "@finance/lib/api";
import Layout, { PageHeader } from "@finance/components/Layout";
import { useAuth } from "@finance/context/AuthContext";
import WorkingCapital from "@finance/components/WorkingCapital";
import ExecTrackerTable from "@finance/components/ExecTrackerTable";
import ExecReports from "@finance/components/ExecReports";
import { Button } from "@finance/components/ui/button";
import { Textarea } from "@finance/components/ui/textarea";
import { Input } from "@finance/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@finance/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@finance/components/ui/tabs";
import { toast } from "sonner";
import { Crown, FolderPlus, Folder, Trash, PencilSimple, CaretLeft, CaretRight, Plus, TextB, TextItalic, TextHOne, TextHTwo, ListBullets, CheckSquare, Eye } from "@phosphor-icons/react";
import { MarkdownView, stripMarkdown } from "@finance/lib/miniMarkdown";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";

export default function Executive() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [txns, setTxns] = useState([]);

  useEffect(() => {
    if (!user?.is_executive) return;
    api.get("/dashboard/summary").then((r) => setSummary(r.data));
    api.get("/transactions").then((r) => setTxns(r.data));
  }, [user]);

  if (user && !user.is_executive) return <Navigate to="/" replace />;

  // client concentration
  const byClient = {};
  txns.forEach((t) => {
    if (!t.client_name) return;
    byClient[t.client_name] = (byClient[t.client_name] || 0) + (t.total_amount || 0);
  });
  const clientData = Object.entries(byClient).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 8);
  const totalRev = clientData.reduce((s, c) => s + c.value, 0);

  return (
    <Layout>
      <PageHeader title="Executive" subtitle="Strategic workspace — Head of Finance only" actions={<Crown size={22} weight="fill" className="text-[#1B4383]" />} />
      <div className="p-8">
        <Tabs defaultValue="dashboard">
          <TabsList data-testid="exec-tabs">
            <TabsTrigger value="dashboard" data-testid="tab-dashboard">Business Progress</TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports">CFO Reports</TabsTrigger>
            <TabsTrigger value="tracker" data-testid="tab-tracker">Executive Tracker</TabsTrigger>
            <TabsTrigger value="journal" data-testid="tab-journal">Notes / Journal</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="mt-6">
            {summary && (
              <div className="soft-card grid grid-cols-2 divide-x divide-y divide-gray-100 lg:grid-cols-4 lg:divide-y-0 dark:divide-[#243049]">
                <ExecKpi label="Open Transactions" value={summary.open_transactions} />
                <ExecKpi label="Payables Outstanding" value={`TSh ${fmtNum(summary.payables_total)}`} />
                <ExecKpi label="Pending Documents" value={summary.pending_documents} />
                <ExecKpi label="Stale Deals" value={summary.stale_transactions.length} />
              </div>
            )}
            <WorkingCapital />
            <div className="mt-6 soft-card p-6">
              <h3 className="font-display text-base font-bold dark:text-white">Client Concentration <span className="text-xs font-normal text-gray-400">· revenue by client</span></h3>
              {clientData[0] && (totalRev > 0) && (clientData[0].value / totalRev > 0.5) && (
                <div className="mt-2 rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
                  ⚠ Over-reliance risk: {clientData[0].name} accounts for {Math.round((clientData[0].value / totalRev) * 100)}% of tracked revenue.
                </div>
              )}
              <div className="mt-4 h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={clientData} layout="vertical" margin={{ left: 40 }}>
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={100} />
                    <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => fmtNum(v)} />
                    <Bar dataKey="value" radius={[0, 2, 2, 0]}>{clientData.map((_, i) => <Cell key={i} fill="#111827" />)}</Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="reports" className="mt-6">
            <ExecReports />
          </TabsContent>

          <TabsContent value="tracker" className="mt-6">
            <ExecTrackerTable />
          </TabsContent>
          <TabsContent value="journal" className="mt-6">
            <ExecJournal />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

function ExecKpi({ label, value }) {
  return (
    <div className="p-5 md:p-6" data-testid="exec-kpi">
      <div className="lx-label">{label}</div>
      <div className="mt-2 kpi-value text-3xl sm:text-4xl">{value}</div>
    </div>
  );
}

function ExecJournal() {
  const [notes, setNotes] = useState([]);
  const [folders, setFolders] = useState([]);
  const [view, setView] = useState("folders");          // folders | folder | note
  const [activeFolder, setActiveFolder] = useState("none");
  const [activeNoteId, setActiveNoteId] = useState(null);
  const [draft, setDraft] = useState("");
  const [draftFolder, setDraftFolder] = useState("none");
  const [isNew, setIsNew] = useState(false);
  const [newFolder, setNewFolder] = useState("");
  const [renameId, setRenameId] = useState(null);
  const [renameText, setRenameText] = useState("");
  const [q, setQ] = useState("");

  const load = async () => {
    try {
      const [n, f] = await Promise.all([api.get("/exec/notes"), api.get("/exec/note-folders")]);
      setNotes(n.data || []); setFolders(f.data || []);
    } catch { setNotes([]); setFolders([]); }
  };
  useEffect(() => { load(); }, []);

  const folderIds = new Set(folders.map((f) => f.id));
  const notesIn = (fid) => notes.filter((n) => (fid === "none" ? !folderIds.has(n.folder_id) : n.folder_id === fid));
  const titleOf = (n) => (stripMarkdown((n.text || "").split("\n")[0]).slice(0, 80) || "Untitled note");
  const previewOf = (n) => (stripMarkdown((n.text || "").split("\n").slice(1).join(" ")).slice(0, 140));
  const folderName = (fid) => (fid === "none" ? "Uncategorized" : (folders.find((f) => f.id === fid)?.name || "Section"));

  const openFolder = (fid) => { setActiveFolder(fid); setView("folder"); };
  const openNote = (n) => { setActiveNoteId(n.id); setDraft(n.text || ""); setDraftFolder(n.folder_id || "none"); setIsNew(false); setView("note"); };
  const openNew = () => { setActiveNoteId(null); setDraft(""); setDraftFolder(activeFolder); setIsNew(true); setView("note"); };

  const saveDraft = async () => {
    if (!draft.trim()) { toast.error("Write something before saving."); return; }
    const folder_id = draftFolder === "none" ? null : draftFolder;
    try {
      if (isNew) {
        const { data } = await api.post("/exec/notes", { text: draft, folder_id });
        setActiveNoteId(data.id); setIsNew(false); toast.success("Note saved");
      } else {
        await api.put(`/exec/notes/${activeNoteId}`, { text: draft, folder_id }); toast.success("Note updated");
      }
      await load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this note?")) return;
    try { await api.delete(`/exec/notes/${id}`); await load(); toast.success("Note deleted"); setView("folder"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const addFolder = async () => {
    if (!newFolder.trim()) return;
    try { await api.post("/exec/note-folders", { name: newFolder.trim() }); setNewFolder(""); await load(); toast.success("Section created"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const saveRename = async (id) => {
    if (!renameText.trim()) return;
    try { await api.put(`/exec/note-folders/${id}`, { name: renameText.trim() }); setRenameId(null); setRenameText(""); await load(); toast.success("Section renamed"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const delFolder = async (id) => {
    if (!window.confirm("Delete this section? Its notes move to Uncategorized (they are not deleted).")) return;
    try { await api.delete(`/exec/note-folders/${id}`); if (activeFolder === id) setActiveFolder("none"); await load(); toast.success("Section deleted"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  // LEVEL 3 — full-page note editor
  if (view === "note") {
    const note = notes.find((n) => n.id === activeNoteId);
    return (
      <JournalNoteEditor
        draft={draft} setDraft={setDraft}
        draftFolder={draftFolder} setDraftFolder={setDraftFolder}
        folders={folders} folderName={folderName} activeFolder={activeFolder}
        isNew={isNew} note={note}
        onBack={() => { setView("folder"); load(); }}
        onSave={saveDraft}
        onDelete={() => del(activeNoteId)}
      />
    );
  }

  // LEVEL 2 — notes inside a folder
  if (view === "folder") {
    const list = notesIn(activeFolder);
    return (
      <div data-testid="journal-folder-view">
        <div className="mb-4 flex items-center justify-between">
          <button className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900" data-testid="journal-folder-back" onClick={() => setView("folders")}>
            <CaretLeft size={16} />All sections
          </button>
          <Button size="sm" className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="journal-new-note" onClick={openNew}><Plus size={14} className="mr-1" />New note</Button>
        </div>
        <div className="mb-4 flex items-center gap-2">
          <Folder size={18} weight="fill" className="text-[#1B4383]" />
          <h3 className="font-display text-lg font-bold">{folderName(activeFolder)}</h3>
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">{list.length}</span>
        </div>
        {list.length === 0 ? (
          <div className="rounded-sm border border-dashed border-gray-300 py-16 text-center text-sm text-gray-400" data-testid="journal-folder-empty">No notes here yet. Click “New note” to add one.</div>
        ) : (
          <div className="space-y-2">
            {list.map((n) => (
              <button key={n.id} data-testid={`journal-note-row-${n.id}`} onClick={() => openNote(n)} className="soft-card soft-card--hover group flex w-full items-start justify-between gap-4 p-4 text-left">
                <div className="min-w-0">
                  <div className="truncate font-medium text-gray-900 dark:text-gray-100">{titleOf(n)}</div>
                  {previewOf(n) && <div className="mt-0.5 truncate text-xs text-gray-500">{previewOf(n)}</div>}
                  <div className="mt-1 text-[11px] text-gray-400">{fmtDate(n.updated_at || n.created_at)}{n.updated_at ? " · edited" : ""}</div>
                </div>
                <CaretRight size={16} className="mt-1 shrink-0 text-gray-300 group-hover:text-[#1B4383]" />
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // LEVEL 1 — folder grid
  const tiles = [...folders.map((f) => ({ id: f.id, name: f.name })), { id: "none", name: "Uncategorized" }];
  const query = q.trim().toLowerCase();
  const matches = query ? notes.filter((n) => (n.text || "").toLowerCase().includes(query)) : [];
  return (
    <div data-testid="journal-folders-view">
      <div className="mb-5 flex flex-wrap items-center gap-2">
        <Input data-testid="journal-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search all notes…" className="max-w-xs" />
        <Input data-testid="journal-new-folder" value={newFolder} onChange={(e) => setNewFolder(e.target.value)} placeholder="New section name…" className="max-w-xs" onKeyDown={(e) => e.key === "Enter" && addFolder()} />
        <Button variant="outline" data-testid="journal-add-folder" onClick={addFolder}><FolderPlus size={16} className="mr-1" />Add Section</Button>
        <span className="ml-auto text-xs text-gray-400">{notes.length} note{notes.length === 1 ? "" : "s"} across {folders.length} section{folders.length === 1 ? "" : "s"}</span>
      </div>
      {query ? (
        <div data-testid="journal-search-results">
          <div className="mb-3 text-sm text-gray-500">{matches.length} result{matches.length === 1 ? "" : "s"} for “{q.trim()}”</div>
          {matches.length === 0 ? (
            <div className="rounded-sm border border-dashed border-gray-300 py-16 text-center text-sm text-gray-400" data-testid="journal-search-empty">No notes match your search.</div>
          ) : (
            <div className="space-y-2">
              {matches.map((n) => (
                <button key={n.id} data-testid={`journal-search-row-${n.id}`} onClick={() => openNote(n)} className="soft-card soft-card--hover group flex w-full items-start justify-between gap-4 p-4 text-left">
                  <div className="min-w-0">
                    <div className="truncate font-medium text-gray-900 dark:text-gray-100">{titleOf(n)}</div>
                    {previewOf(n) && <div className="mt-0.5 truncate text-xs text-gray-500">{previewOf(n)}</div>}
                    <div className="mt-1 text-[11px] text-gray-400">{folderName(folderIds.has(n.folder_id) ? n.folder_id : "none")} · {fmtDate(n.updated_at || n.created_at)}</div>
                  </div>
                  <CaretRight size={16} className="mt-1 shrink-0 text-gray-300 group-hover:text-[#1B4383]" />
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {tiles.map((t) => (
          <div key={t.id} data-testid={`journal-folder-tile-${t.id}`} className="soft-card soft-card--hover group relative cursor-pointer p-6" onClick={() => openFolder(t.id)}>
            <div className="flex items-start justify-between">
              <Folder size={28} weight="fill" className="text-[#1B4383]" />
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600">{notesIn(t.id).length}</span>
            </div>
            {renameId === t.id ? (
              <Input autoFocus data-testid={`journal-rename-input-${t.id}`} value={renameText} onChange={(e) => setRenameText(e.target.value)} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => { if (e.key === "Enter") saveRename(t.id); }} className="mt-3 h-7 text-sm" />
            ) : (
              <div className="mt-3 font-display text-base font-bold text-gray-900" data-testid={`journal-folder-name-${t.id}`}>{t.name}</div>
            )}
            <div className="mt-1 text-xs text-gray-400">{notesIn(t.id).length} note{notesIn(t.id).length === 1 ? "" : "s"}</div>
            {t.id !== "none" && (
              <div className="mt-3 flex items-center gap-3 opacity-0 transition-opacity group-hover:opacity-100">
                {renameId === t.id ? (
                  <>
                    <button className="text-xs text-[#1B4383] hover:underline" data-testid={`journal-rename-save-${t.id}`} onClick={(e) => { e.stopPropagation(); saveRename(t.id); }}>Save</button>
                    <button className="text-xs text-gray-400 hover:underline" onClick={(e) => { e.stopPropagation(); setRenameId(null); setRenameText(""); }}>Cancel</button>
                  </>
                ) : (
                  <>
                    <button className="text-gray-400 hover:text-blue-600" data-testid={`journal-rename-${t.id}`} onClick={(e) => { e.stopPropagation(); setRenameId(t.id); setRenameText(t.name); }}><PencilSimple size={14} /></button>
                    <button className="text-gray-400 hover:text-red-500" data-testid={`journal-folder-delete-${t.id}`} onClick={(e) => { e.stopPropagation(); delFolder(t.id); }}><Trash size={14} /></button>
                  </>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      )}
    </div>
  );
}

function JournalNoteEditor({ draft, setDraft, draftFolder, setDraftFolder, folders, folderName, activeFolder, isNew, note, onBack, onSave, onDelete }) {
  const taRef = useRef(null);
  const [mode, setMode] = useState("write");

  const autosize = () => {
    const el = taRef.current;
    if (el) { el.style.height = "auto"; el.style.height = `${Math.max(240, el.scrollHeight)}px`; }
  };
  useEffect(() => { if (mode === "write") autosize(); }, [draft, mode]);

  const wrap = (b, a) => {
    const el = taRef.current; if (!el) return;
    const s = el.selectionStart, e = el.selectionEnd;
    const sel = draft.slice(s, e) || "text";
    const next = draft.slice(0, s) + b + sel + a + draft.slice(e);
    setDraft(next);
    requestAnimationFrame(() => { el.focus(); el.selectionStart = s + b.length; el.selectionEnd = s + b.length + sel.length; });
  };
  const prefixLine = (prefix) => {
    const el = taRef.current; if (!el) return;
    const s = el.selectionStart;
    const lineStart = draft.lastIndexOf("\n", s - 1) + 1;
    const next = draft.slice(0, lineStart) + prefix + draft.slice(lineStart);
    setDraft(next);
    requestAnimationFrame(() => { el.focus(); el.selectionStart = el.selectionEnd = s + prefix.length; });
  };

  const tools = [
    { icon: TextHOne, fn: () => prefixLine("# "), t: "Heading", tid: "fmt-h1" },
    { icon: TextHTwo, fn: () => prefixLine("## "), t: "Subheading", tid: "fmt-h2" },
    { icon: TextB, fn: () => wrap("**", "**"), t: "Bold", tid: "fmt-bold" },
    { icon: TextItalic, fn: () => wrap("*", "*"), t: "Italic", tid: "fmt-italic" },
    { icon: ListBullets, fn: () => prefixLine("- "), t: "Bullet", tid: "fmt-bullet" },
    { icon: CheckSquare, fn: () => prefixLine("- [ ] "), t: "Checkbox", tid: "fmt-check" },
  ];

  const edited = note?.updated_at || note?.created_at;

  return (
    <div data-testid="journal-note-editor">
      <div className="mb-4 flex items-center justify-between">
        <button className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900" data-testid="journal-note-back" onClick={onBack}>
          <CaretLeft size={16} />Back to {folderName(activeFolder)}
        </button>
        <div className="flex items-center gap-2">
          {!isNew && <Button variant="outline" size="sm" className="text-red-500 hover:text-red-600" data-testid="journal-note-delete" onClick={onDelete}><Trash size={14} className="mr-1" />Delete</Button>}
          <Button size="sm" className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="journal-note-save" onClick={onSave}>Save</Button>
        </div>
      </div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="text-xs text-gray-400">Section</span>
        <Select value={draftFolder} onValueChange={setDraftFolder}>
          <SelectTrigger className="h-8 w-56 text-xs" data-testid="journal-note-folder"><SelectValue placeholder="Uncategorized" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Uncategorized</SelectItem>
            {folders.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {!isNew && edited && <span className="ml-auto text-xs text-gray-400" data-testid="journal-note-edited">Last edited {fmtDate(edited)}{note?.updated_at ? "" : " (created)"}</span>}
      </div>

      <div className="rounded-t-sm border border-b-0 border-gray-200 bg-gray-50 p-1.5">
        <div className="flex items-center gap-1">
          {mode === "write" && tools.map((tl) => (
            <button key={tl.tid} type="button" title={tl.t} data-testid={tl.tid} onClick={tl.fn}
              className="grid h-8 w-8 place-items-center rounded-sm text-gray-600 transition-colors hover:bg-white hover:text-[#1B4383]"><tl.icon size={16} /></button>
          ))}
          <div className="ml-auto inline-flex rounded-sm border border-gray-200 bg-white p-0.5">
            <button type="button" data-testid="journal-mode-write" onClick={() => setMode("write")} className={`inline-flex items-center gap-1 rounded-[3px] px-2 py-1 text-xs font-semibold ${mode === "write" ? "bg-[#1B4383] text-white" : "text-gray-500 hover:bg-gray-100"}`}><PencilSimple size={13} />Write</button>
            <button type="button" data-testid="journal-mode-preview" onClick={() => setMode("preview")} className={`inline-flex items-center gap-1 rounded-[3px] px-2 py-1 text-xs font-semibold ${mode === "preview" ? "bg-[#1B4383] text-white" : "text-gray-500 hover:bg-gray-100"}`}><Eye size={13} />Preview</button>
          </div>
        </div>
      </div>

      {mode === "write" ? (
        <Textarea
          ref={taRef}
          autoFocus
          data-testid="journal-note-textarea"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="First line becomes the title. Use the toolbar for headings, bold, bullets and checkboxes."
          className="w-full resize-none overflow-hidden rounded-t-none border-gray-200 text-sm leading-relaxed"
        />
      ) : (
        <div className="min-h-[240px] rounded-b-sm border border-gray-200 bg-white p-4" data-testid="journal-note-preview">
          {draft.trim() ? <MarkdownView text={draft} /> : <p className="text-sm text-gray-400">Nothing to preview yet.</p>}
        </div>
      )}
      <p className="mt-2 text-xs text-gray-400">Tip: the first line becomes the note's title. Toolbar adds simple formatting (headings, <span className="font-semibold">bold</span>, bullets, checkboxes).</p>
    </div>
  );
}
